import {
  useQuery as useApolloQuery,
  useMutation as useApolloMutation,
  type DocumentNode,
  type OperationVariables,
  type QueryHookOptions,
  type MutationHookOptions,
  type ApolloError,
  type BaseMutationOptions,
} from '@apollo/client';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { metrics } from '@seek/metrics-js';
import { useTranslations } from '@vocab/react';
import { useToast } from 'braid-design-system';
import { getOperationAST } from 'graphql';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { updateRoute } from 'src/components/Router/actionCreators';
import type { UpdateRouteParams } from 'src/components/Router/reducer';
import { queryFailure } from 'src/reducers/loading/actionCreators';

import { useAppConfig } from '../appConfig';
import {
  handleError,
  isGraphQLAuthError,
  useHandleError,
} from '../error-handling/error-handling';
import { sendApolloErrorMetrics } from '../request-metrics';
import { useRetryActionStore } from '../retry-actions/RetryActionProvider';
import type { RetryActionKey } from '../retry-actions/reducer';

import translations from './.vocab';
import { useParentTitles } from './error-handling/errorHandling';

export const getOperationName = (query: DocumentNode) => {
  const operation = getOperationAST(query);
  return operation?.name?.value ?? 'Unknown';
};

export const useQuery = <TData = any, TVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, OperationVariables> & {
    bypassErrorPageOnFail?: boolean;
    eatAuthError?: boolean;
  },
) => {
  const dispatch = useDispatch();
  const operationName = getOperationName(query);

  const queryResult = useApolloQuery(query, {
    ...options,
  });

  useEffect(() => {
    if (
      queryResult.error &&
      !isGraphQLAuthError(queryResult.error) &&
      options?.bypassErrorPageOnFail !== true
    ) {
      dispatch(queryFailure(operationName));
    }
  }, [
    dispatch,
    queryResult.error,
    operationName,
    options?.bypassErrorPageOnFail,
  ]);

  const shouldEatAuthError =
    queryResult.error &&
    options?.eatAuthError === true &&
    isGraphQLAuthError(queryResult.error);

  const error = shouldEatAuthError ? undefined : queryResult.error;

  useEffect(() => {
    if (error) {
      sendApolloErrorMetrics({
        type: 'query',
        name: operationName,
        error,
      });
    }
  }, [error, operationName]);

  useHandleError(error, {
    kind: 'GraphQueryError',
    name: operationName,
    input: options?.variables || {},
  });

  return queryResult;
};

export const useMutation = <TData = any, TVariables = OperationVariables>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: MutationHookOptions<TData, TVariables>,
) => {
  const { zone, language, isUnifiedSite } = useAppConfig();
  const operationName = getOperationName(mutation);

  const mutationResult = useApolloMutation(mutation, {
    ...options,
    onError: (error: ApolloError, clientOptions?: BaseMutationOptions) => {
      sendApolloErrorMetrics({
        type: 'mutation',
        name: operationName,
        error,
      });
      handleError(error, zone, language, isUnifiedSite, {
        kind: 'GraphMutationError',
        name: operationName,
        input: clientOptions?.variables || {},
      });
      if (options && options.onError) {
        options.onError(error);
      }
    },
  });

  return mutationResult;
};

type ToastMutationOptions<
  TData = any,
  TVariables = OperationVariables,
  MutationMetadata = undefined,
> = {
  key: RetryActionKey;
  errorActionRouteParameters?: UpdateRouteParams;
  onMutate?: (
    variables: TVariables,
    mutationMetadata?: MutationMetadata,
  ) => void;
  onCompleted?: (data: TData, mutationMetadata?: Record<string, any>) => void;
  onError?: (error: ApolloError, clientOptions?: BaseMutationOptions) => void;
  successToastMessage?: string;
  failToastMessage?: string;
} & Pick<
  MutationHookOptions<TData, TVariables>,
  'optimisticResponse' | 'update'
>;

export const useToastMutation = <
  TData = any,
  TVariables = OperationVariables,
  MutationMetadata = undefined,
>(
  mutation: TypedDocumentNode<TData, TVariables>,
  options: ToastMutationOptions<TData, TVariables, MutationMetadata>,
) => {
  const showToast = useToast();
  const { key, onMutate, successToastMessage, failToastMessage } = options;
  const { t } = useTranslations(translations);
  const parentTitles = useParentTitles();
  const { dispatch } = useRetryActionStore();
  const reduxDispatch = useDispatch();

  const onCompleted = useCallback(
    (data: TData, mutationMetadata?: Record<string, any>) => {
      options?.onCompleted?.(data, mutationMetadata);
      dispatch({ type: 'deleteRetryAction', payload: { key } });

      if (successToastMessage) {
        showToast({
          tone: 'positive',
          message: successToastMessage,
          key,
        });
      }
    },
    [options, dispatch, key, showToast, successToastMessage],
  );

  const onError = useCallback(
    (error: ApolloError, clientOptions?: BaseMutationOptions) => {
      options?.onError?.(error, clientOptions);
      const toastName = parentTitles[key];

      dispatch({ type: 'setCanRetry', payload: { canRetry: true, key } });

      const action = options?.errorActionRouteParameters
        ? {
            label: t('Go to toastName', { toastName }),
            onClick: () => {
              metrics.count('mutation-failure-toast-action', [`id:${key}`]);
              if (options?.errorActionRouteParameters) {
                const { errorActionRouteParameters } = options;
                reduxDispatch(
                  updateRoute({
                    ...errorActionRouteParameters,
                    params: {
                      ...errorActionRouteParameters.params,
                      ...(errorActionRouteParameters.mode === 'READ'
                        ? { allowScroll: true }
                        : {}),
                    },
                  }),
                );
              }
            },
          }
        : undefined;

      showToast({
        tone: 'critical',
        message: failToastMessage
          ? failToastMessage
          : t('toastName could not be saved', { toastName }),
        action,
        key,
      });
    },
    [
      dispatch,
      failToastMessage,
      key,
      options,
      parentTitles,
      reduxDispatch,
      showToast,
      t,
    ],
  );

  const [runMutation, response] = useMutation(mutation, {
    ...options,
    onCompleted,
    onError,
  });

  const updateFunction = useCallback(
    (variables: TVariables, mutationMetadata?: MutationMetadata) => {
      const action = () => {
        onMutate?.(variables, mutationMetadata);
        runMutation({
          variables: {
            ...variables,
            ...(mutationMetadata && { mutationMetadata }),
          },
        });
      };
      const retryAction = { action, canRetry: false };

      dispatch({ type: 'setRetryAction', payload: { retryAction, key } });
      action();
    },
    [dispatch, key, onMutate, runMutation],
  );

  return [updateFunction, response] as const;
};
